import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiChevronDown, FiPlus, FiTrash2, FiChevronUp, FiFileText, FiTruck, FiAlertCircle, FiClipboard } from 'react-icons/fi';
import api from '../../services/api';
import AccountingTab from '../truck/AccountingTab';
import DocCenterTab from '../truck/DocCenterTab';
import StatusSelector from '../../components/common/StatusSelector';
import TradePartnerAutocomplete from '../../components/common/TradePartnerAutocomplete';

const AirExportDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'Basic');
  const [hawbs, setHawbs] = useState([]);
  const [expandedHawbs, setExpandedHawbs] = useState({});
  const [showTools, setShowTools] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const initialLoadDone = useRef(false);
  const toolsRef = useRef(null);

  const emptyShipment = {
    file_no: '', mawb_no: '', hawb_no: '', status: 'Active',
    shipper: '', consignee: '', notify_party: '', customer: '',
    forwarding_agent: '', customs_broker: '', carrier: '',
    departure: '', destination: '', flight_no: '',
    departure_date_time: '', arrival_date_time: '', etd: '', eta: '',
    freight_location: '',
    gross_weight_kg: '', chargeable_weight_kg: '', pieces: '', description: '',
    freight_terms: '', service_type: '', trade_terms: '',
    memo: '', instruction: '', entry_no: '', po_no: '', trucker: '',
  };

  const [formData, setFormData] = useState(emptyShipment);
  const [shipmentId, setShipmentId] = useState(id || null);

  useEffect(() => {
    if (id) { loadShipment(id); }
    else { setTimeout(() => { initialLoadDone.current = true; }, 0); }
  }, [id]);

  useEffect(() => {
    if (initialLoadDone.current) setIsDirty(true);
  }, [formData]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setShowTools(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isNew && !isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isNew, isDirty]);

  const loadShipment = async (sid) => {
    try {
      const res = await api.get(`/air-export/${sid}`);
      const d = res.data;
      const mapped = {};
      Object.keys(emptyShipment).forEach(k => {
        mapped[k] = d[k] || '';
      });
      setFormData(mapped); setShipmentId(d.id);
      if (d.hawbs?.length > 0) {
        setHawbs(d.hawbs);
        const ex = {}; d.hawbs.forEach(h => { ex[h.id] = true; }); setExpandedHawbs(ex);
      }
    } catch (error) { toast.error('Failed to load shipment'); navigate('/air-export'); }
    finally { setLoading(false); setTimeout(() => { initialLoadDone.current = true; }, 0); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setIsDirty(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (isNew) {
        const { file_no, ...createData } = formData;
        const res = await api.post('/air-export', createData);
        const data = res.data;
        setShipmentId(data.id);
        setFormData(prev => ({ ...prev, file_no: data.file_no }));
        setIsDirty(false);
        toast.success(`Shipment ${data.file_no} created`);
        navigate(`/air-export/${data.id}`, { replace: true });
      } else {
        const { file_no, ...updateData } = formData;
        await api.put(`/air-export/${shipmentId}`, updateData);
        setIsDirty(false);
        toast.success('Shipment saved');
      }
    } catch (error) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  // HAWB CRUD
  const addHawb = async () => {
    try {
      const res = await api.post(`/air-export/${shipmentId}/hawbs`, {});
      setHawbs(prev => [...prev, res.data]);
      setExpandedHawbs(prev => ({ ...prev, [res.data.id]: true }));
      toast.success('HAWB added');
    } catch (error) { toast.error('Failed to add HAWB'); }
  };

  const handleHawbChange = (hid, e) => {
    const { name, value, type, checked } = e.target;
    setHawbs(prev => prev.map(h => h.id === hid ? { ...h, [name]: type === 'checkbox' ? checked : value } : h));
  };

  const setHawbField = (hid, name, value) => {
    setHawbs(prev => prev.map(h => h.id === hid ? { ...h, [name]: value } : h));
  };

  const saveHawb = async (hid) => {
    const hawb = hawbs.find(h => h.id === hid);
    if (!hawb) return;
    try {
      const { id: _, shipment_id, created_at, updated_at, ...ud } = hawb;
      await api.put(`/air-export/hawbs/${hid}`, ud);
      toast.success('HAWB saved');
    } catch (error) { toast.error('Failed to save HAWB'); }
  };

  const deleteHawb = async (hid) => {
    if (!window.confirm('Delete this HAWB?')) return;
    try {
      await api.delete(`/air-export/hawbs/${hid}`);
      setHawbs(prev => prev.filter(h => h.id !== hid));
      toast.success('HAWB deleted');
    } catch (error) { toast.error('Failed to delete HAWB'); }
  };

  const toggleHawb = (hid) => setExpandedHawbs(prev => ({ ...prev, [hid]: !prev[hid] }));

  if (loading) return <div className="loading"><div className="loading-spinner"></div></div>;

  return (
    <div className="shipment-page">
      <div className="shipment-breadcrumb">
        <button className="breadcrumb-back-btn" onClick={() => {
          if ((isNew || isDirty) && !window.confirm('You are leaving the page without saving. Are you sure?')) return;
          navigate('/air-export');
        }}><FiArrowLeft /></button>
        <span>Home</span><span className="breadcrumb-sep">&gt;</span>
        <span>Air Export</span><span className="breadcrumb-sep">&gt;</span>
        <span className="breadcrumb-current">{formData.file_no || 'New'}</span>
      </div>

      <div className="shipment-tabs">
        {['Basic', 'Accounting', 'Doc Center'].map(tab => (
          <button key={tab} className={`shipment-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>{tab}</button>
        ))}
      </div>

      <div className="shipment-header-bar">
        <div className="header-left">
          <span className="header-mbl"><span className="mbl-label">MAWB</span><span className="mbl-value">{formData.mawb_no || '-'}</span></span>
          <span className="header-customer">
            <span className="customer-label">{formData.departure || '-'} ({formData.etd || '-'})</span>
            <span style={{ margin: '0 4px', color: '#94a3b8' }}>&#9658;</span>
            <span className="customer-value">{formData.destination || '-'} ({formData.eta || '-'})</span>
          </span>
        </div>
        <div className="header-right">
          <StatusSelector
            value={formData.status}
            options={['Active', 'In Transit', 'Departed', 'Delivered', 'Closed']}
            onChange={(val) => { setFormData(prev => ({ ...prev, status: val })); setIsDirty(true); }}
          />
          <span className="header-route">&#9992; {formData.carrier || '-'} &middot; {formData.flight_no || '-'}</span>
          <div ref={toolsRef} style={{ position: 'relative' }}>
            <button className="header-tools-btn" onClick={() => setShowTools(!showTools)}>
              Tools <FiChevronDown />
            </button>
            {showTools && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, zIndex: 200,
                background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '220px',
                marginTop: '4px', overflow: 'hidden',
              }}>
                <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>Documents</div>
                <button onClick={() => { setShowTools(false); window.open(`/documents/air-export/${shipmentId}/delivery-order`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiTruck size={15} style={{ color: '#2563eb' }} /> Pickup / Delivery Order
                </button>
                <button onClick={() => { setShowTools(false); window.open(`/documents/air-export/${shipmentId}/bill-of-lading`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiClipboard size={15} style={{ color: '#059669' }} /> Bill of Lading
                </button>
                <button onClick={() => { setShowTools(false); window.open(`/documents/air-export/${shipmentId}/arrival-notice`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiAlertCircle size={15} style={{ color: '#d97706' }} /> Arrival Notice
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ==================== BASIC TAB ==================== */}
      {activeTab === 'Basic' && (
        <div className="oi-basic-content">
          <form onSubmit={handleSave} className="shipment-form">
            <div className="oi-mbl-section">

              <div className="form-section-header">General Information</div>
              <div className="shipment-form-grid">
                <FG label="File No." value={formData.file_no} disabled />
                <FG label="MAWB No." name="mawb_no" value={formData.mawb_no} onChange={handleChange} />
                <FG label="HAWB No." name="hawb_no" value={formData.hawb_no} onChange={handleChange} />
                <div className="shipment-form-group"><label>Shipper</label>
                  <TradePartnerAutocomplete value={formData.shipper} tpTypes="Customer,Vendor" placeholder="Search shipper..."
                    onChange={(val) => setFormData(prev => ({ ...prev, shipper: val }))} /></div>
                <div className="shipment-form-group"><label>Consignee</label>
                  <TradePartnerAutocomplete value={formData.consignee} tpTypes="Customer,Vendor" placeholder="Search consignee..."
                    onChange={(val) => setFormData(prev => ({ ...prev, consignee: val }))} /></div>
                <div className="shipment-form-group"><label>Notify Party</label>
                  <TradePartnerAutocomplete value={formData.notify_party} tpTypes="Customer,Vendor" placeholder="Search notify party..."
                    onChange={(val) => setFormData(prev => ({ ...prev, notify_party: val }))} /></div>
                <div className="shipment-form-group"><label>Customer</label>
                  <TradePartnerAutocomplete value={formData.customer} placeholder="Search customer..."
                    onChange={(val) => setFormData(prev => ({ ...prev, customer: val }))} /></div>
                <div className="shipment-form-group"><label>Forwarding Agent</label>
                  <TradePartnerAutocomplete value={formData.forwarding_agent} tpTypes="Customer,Vendor" placeholder="Search forwarding agent..."
                    onChange={(val) => setFormData(prev => ({ ...prev, forwarding_agent: val }))} /></div>
                <div className="shipment-form-group"><label>Customs Broker</label>
                  <TradePartnerAutocomplete value={formData.customs_broker} tpTypes="Customer,Vendor" placeholder="Search customs broker..."
                    onChange={(val) => setFormData(prev => ({ ...prev, customs_broker: val }))} /></div>
                <FG label="Carrier" name="carrier" value={formData.carrier} onChange={handleChange} />
                <div className="shipment-form-group"><label>Trucker</label>
                  <TradePartnerAutocomplete value={formData.trucker} tpTypes="Trucker" placeholder="Search trucker..."
                    onChange={(val) => setFormData(prev => ({ ...prev, trucker: val }))} /></div>
              </div>

              <div className="form-section-header">Flight Information</div>
              <div className="shipment-form-grid">
                <div className="shipment-form-group"><label>Departure</label>
                  <TradePartnerAutocomplete value={formData.departure} placeholder="Departure..."
                    onChange={(val) => setFormData(prev => ({ ...prev, departure: val }))} /></div>
                <div className="shipment-form-group"><label>Destination</label>
                  <TradePartnerAutocomplete value={formData.destination} placeholder="Destination..."
                    onChange={(val) => setFormData(prev => ({ ...prev, destination: val }))} /></div>
                <FG label="Flight No." name="flight_no" value={formData.flight_no} onChange={handleChange} />
                <FG label="Departure Date/Time" name="departure_date_time" value={formData.departure_date_time} onChange={handleChange} type="datetime-local" />
                <FG label="Arrival Date/Time" name="arrival_date_time" value={formData.arrival_date_time} onChange={handleChange} type="datetime-local" />
                <FG label="ETD" name="etd" value={formData.etd} onChange={handleChange} type="date" />
                <FG label="ETA" name="eta" value={formData.eta} onChange={handleChange} type="date" />
                <div className="shipment-form-group"><label>Freight Location</label>
                  <TradePartnerAutocomplete value={formData.freight_location} placeholder="Freight location..."
                    onChange={(val) => setFormData(prev => ({ ...prev, freight_location: val }))} /></div>
              </div>

              <div className="form-section-header">Cargo</div>
              <div className="shipment-form-grid">
                <FG label="Pieces" name="pieces" value={formData.pieces} onChange={handleChange} />
                <FG label="Gross Weight (KG)" name="gross_weight_kg" value={formData.gross_weight_kg} onChange={handleChange} />
                <FG label="Chargeable Weight (KG)" name="chargeable_weight_kg" value={formData.chargeable_weight_kg} onChange={handleChange} />
                <div className="shipment-form-group">
                  <label>Description</label>
                  <textarea name="description" value={formData.description || ''} onChange={handleChange} rows={2}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical', color: '#1e293b' }} />
                </div>
              </div>

              <div className="form-section-header">Freight & Terms</div>
              <div className="shipment-form-grid">
                <FGSelect label="Freight Terms" name="freight_terms" value={formData.freight_terms} onChange={handleChange} options={[['','--'],['PREPAID','PREPAID'],['COLLECT','COLLECT']]} />
                <FG label="Service Type" name="service_type" value={formData.service_type} onChange={handleChange} />
                <FG label="Trade Terms" name="trade_terms" value={formData.trade_terms} onChange={handleChange} />
                <FG label="Entry No." name="entry_no" value={formData.entry_no} onChange={handleChange} />
                <FG label="P.O. No." name="po_no" value={formData.po_no} onChange={handleChange} />
              </div>

              <div className="form-section-header">Memo / Instruction</div>
              <div className="shipment-form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="shipment-form-group">
                  <label>Memo</label>
                  <textarea name="memo" value={formData.memo || ''} onChange={handleChange} rows={3}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical', color: '#1e293b' }} />
                </div>
                <div className="shipment-form-group">
                  <label>Instruction</label>
                  <textarea name="instruction" value={formData.instruction || ''} onChange={handleChange} rows={3}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical', color: '#1e293b' }} />
                </div>
              </div>

              <div className="shipment-form-actions">
                <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : 'Save Shipment'}</button>
              </div>
            </div>

            {/* ==================== HAWB Section ==================== */}
            <div className="oi-hbl-section">
              <div className="oi-hbl-header">
                <span className="oi-hbl-title">House AWB ({hawbs.length})</span>
                <button type="button" className="btn-add-hbl" onClick={addHawb} disabled={isNew} title={isNew ? 'Save shipment first' : ''}><FiPlus size={13} /> Add HAWB</button>
              </div>

              {hawbs.length === 0 && (
                <div className="oi-hbl-empty">No House AWB added yet. Click "+ Add HAWB" to create one.</div>
              )}

              {hawbs.map((hawb) => (
                <div className="oi-hbl-card" key={hawb.id}>
                  <div className="oi-hbl-card-header" onClick={() => toggleHawb(hawb.id)}>
                    <div className="oi-hbl-card-info">
                      <span className="oi-hbl-no">{hawb.hawb_no || '(No HAWB No.)'}</span>
                      <span className="oi-hbl-customer">{hawb.consignee || ''}</span>
                    </div>
                    <div className="oi-hbl-card-actions">
                      <button type="button" className="btn-delete-hbl" onClick={(e) => { e.stopPropagation(); deleteHawb(hawb.id); }}><FiTrash2 size={13} /></button>
                      {expandedHawbs[hawb.id] ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                    </div>
                  </div>

                  {expandedHawbs[hawb.id] && (
                    <div className="oi-hbl-card-body">
                      <div className="form-section-header">General</div>
                      <div className="shipment-form-grid">
                        <HF label="HAWB No." name="hawb_no" hbl={hawb} onChange={handleHawbChange} required />
                        <div className="shipment-form-group"><label>Shipper</label>
                          <TradePartnerAutocomplete value={hawb.shipper} tpTypes="Customer,Vendor" placeholder="Search shipper..."
                            onChange={(val) => setHawbField(hawb.id, 'shipper', val)} /></div>
                        <div className="shipment-form-group"><label>Consignee</label>
                          <TradePartnerAutocomplete value={hawb.consignee} tpTypes="Customer,Vendor" placeholder="Search consignee..."
                            onChange={(val) => setHawbField(hawb.id, 'consignee', val)} /></div>
                        <div className="shipment-form-group"><label>Notify Party</label>
                          <TradePartnerAutocomplete value={hawb.notify_party} tpTypes="Customer,Vendor" placeholder="Search notify party..."
                            onChange={(val) => setHawbField(hawb.id, 'notify_party', val)} /></div>
                      </div>

                      <div className="form-section-header">Cargo</div>
                      <div className="shipment-form-grid">
                        <HF label="Pieces" name="pieces" hbl={hawb} onChange={handleHawbChange} />
                        <HF label="Gross Weight (KG)" name="gross_weight_kg" hbl={hawb} onChange={handleHawbChange} />
                        <HF label="Chargeable Weight (KG)" name="chargeable_weight_kg" hbl={hawb} onChange={handleHawbChange} />
                        <div className="shipment-form-group">
                          <label>Description</label>
                          <textarea name="description" value={hawb.description || ''} onChange={(e) => handleHawbChange(hawb.id, e)} rows={2}
                            style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical' }} />
                        </div>
                      </div>

                      <div className="form-section-header">Destination</div>
                      <div className="shipment-form-grid">
                        <div className="shipment-form-group"><label>Freight Location</label>
                          <TradePartnerAutocomplete value={hawb.freight_location} placeholder="Freight location..."
                            onChange={(val) => setHawbField(hawb.id, 'freight_location', val)} /></div>
                        <div className="shipment-form-group"><label>Final Destination</label>
                          <TradePartnerAutocomplete value={hawb.final_destination} placeholder="Final destination..."
                            onChange={(val) => setHawbField(hawb.id, 'final_destination', val)} /></div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 0' }}>
                        <button type="button" className="btn-save" onClick={() => saveHawb(hawb.id)}>Save HAWB</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </form>
        </div>
      )}

      {activeTab === 'Accounting' && shipmentId && <AccountingTab shipmentId={shipmentId} shipmentType="air_export" />}
      {activeTab === 'Accounting' && !shipmentId && <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the shipment first to manage accounting.</div>}
      {activeTab === 'Doc Center' && shipmentId && <DocCenterTab shipmentId={shipmentId} shipmentType="air-export" />}
      {activeTab === 'Doc Center' && !shipmentId && <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the shipment first to access documents.</div>}
    </div>
  );
};

// Helper Components
const FG = ({ label, name, value, onChange, required, disabled, type = 'text' }) => (
  <div className="shipment-form-group">
    <label className={required ? 'required' : ''}>{label}</label>
    <input type={type} name={name} value={value || ''} onChange={onChange} disabled={disabled} />
  </div>
);
const FGSelect = ({ label, name, value, onChange, options }) => (
  <div className="shipment-form-group"><label>{label}</label><select name={name} value={value || ''} onChange={onChange}>{options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
);

// HAWB field helpers
const HF = ({ label, name, hbl, onChange, required }) => (
  <div className="shipment-form-group"><label className={required ? 'required' : ''}>{label}</label><input type="text" name={name} value={hbl[name] || ''} onChange={(e) => onChange(hbl.id, e)} /></div>
);

const toolsMenuItemStyle = {
  display: 'flex', alignItems: 'center', gap: '10px',
  width: '100%', padding: '10px 14px', border: 'none',
  background: 'transparent', cursor: 'pointer', fontSize: '13px',
  fontWeight: 500, color: '#334155', textAlign: 'left',
  transition: 'background 0.15s',
};

export default AirExportDetail;
